/* eslint-disable */
/**
 * Stage 73.20q — Loan / Advance Policy Master
 *
 * Per-designation policy editor for loans/advances. Admin picks a
 * designation, then defines which loan_types are allowed plus the
 * max principal + max tenure caps. The ESS Loan Request form drives
 * its loan_type dropdown from this; admin-created loans run through
 * the same gate.
 */
import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Plus, Trash2, Save, Loader2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LOAN_LABEL_DEFAULTS = {
  salary_advance: 'Salary Advance',
  festival_advance: 'Festival Advance',
  emergency_advance: 'Emergency Advance',
  personal_loan: 'Personal Loan',
  housing_loan: 'Housing Loan',
  vehicle_loan: 'Vehicle Loan',
  education_loan: 'Education Loan',
  medical_loan: 'Medical Loan',
  other: 'Other',
};

export default function LoanPolicy() {
  const [selectedCompany] = useActiveCompany();
  // Stage 73.20y — mode toggle (designation | grade)
  const [mode, setMode] = useState('designation');
  const [designations, setDesignations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [meta, setMeta] = useState({ loan_types: [] });
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [items, setItems] = useState([]);
  const [policyNotes, setPolicyNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, [selectedCompany, mode]);

  const loadAll = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const [d, g, p, m] = await Promise.all([
        axios.get(`${API}/masters/designations?company_id=${selectedCompany}`, { withCredentials: true }),
        axios.get(`${API}/masters/grades?company_id=${selectedCompany}`, { withCredentials: true }),
        axios.get(`${API}/loan-policies?company_id=${selectedCompany}&mapping_key=${mode}`, { withCredentials: true }),
        axios.get(`${API}/loan-policies/_meta`, { withCredentials: true }),
      ]);
      setDesignations(d.data || []);
      setGrades(g.data || []);
      setPolicies(p.data || []);
      setMeta(m.data || { loan_types: [] });
      setSelectedKeyId('');
      setItems([]);
      setPolicyNotes('');
    } catch (e) {
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  const keyItems = mode === 'grade' ? grades : designations;
  const keyLabelSingular = mode === 'grade' ? 'Grade' : 'Designation';

  const openPolicy = (keyId) => {
    setSelectedKeyId(keyId);
    const matchField = mode === 'grade' ? 'grade_id' : 'designation_id';
    const existing = policies.find(p => p[matchField] === keyId);
    setItems(existing?.items || []);
    setPolicyNotes(existing?.notes || '');
  };

  const addItem = () => {
    const remaining = meta.loan_types.filter(t => !items.find(it => it.loan_type === t));
    setItems([...items, {
      loan_type: remaining[0] || meta.loan_types[0] || 'salary_advance',
      label: '',
      max_principal: 50000,
      max_tenure_months: 12,
      min_tenure_months: 1,
      interest_rate_default: 0,
      requires_special_approval_above: null,
      notes: '',
    }]);
  };

  const updateItem = (idx, patch) => {
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const savePolicy = async () => {
    if (!selectedKeyId) return;
    if (items.length === 0) {
      toast.error('Add at least one policy item');
      return;
    }
    setSaving(true);
    try {
      const target = keyItems.find(d => d.id === selectedKeyId);
      const payload = {
        mapping_key: mode,
        company_id: selectedCompany,
        items: items.map(it => ({
          ...it,
          max_principal: parseFloat(it.max_principal) || 0,
          max_tenure_months: parseInt(it.max_tenure_months) || 1,
          min_tenure_months: parseInt(it.min_tenure_months) || 1,
          interest_rate_default: parseFloat(it.interest_rate_default) || 0,
          requires_special_approval_above:
            it.requires_special_approval_above === '' || it.requires_special_approval_above == null
              ? null
              : parseFloat(it.requires_special_approval_above),
        })),
        notes: policyNotes,
      };
      if (mode === 'grade') {
        payload.grade_id = selectedKeyId;
        payload.grade_name = target?.name || '';
      } else {
        payload.designation_id = selectedKeyId;
        payload.designation_name = target?.name || '';
      }
      await axios.post(`${API}/loan-policies`, payload, { withCredentials: true });
      toast.success('Advance policy saved');
      await loadAll();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const deletePolicy = async () => {
    if (!selectedKeyId) return;
    if (!window.confirm(`Delete the advance policy for this ${keyLabelSingular.toLowerCase()}? All future advance requests will fall through to standard flow.`)) return;
    try {
      await axios.delete(`${API}/loan-policies/${selectedKeyId}?mapping_key=${mode}`, { withCredentials: true });
      toast.success('Advance policy deleted');
      setItems([]);
      setPolicyNotes('');
      await loadAll();
    } catch (e) {
      toast.error('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 p-6" data-testid="loan-policy-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-blue-600" />
              Advance Policy Master
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Set allowed advance types + principal &amp; tenure caps per {keyLabelSingular.toLowerCase()}. Requests that exceed the cap route for special approval.
            </p>
          </div>
          {/* Mode toggle — shared with Reimbursement Policy via /payroll/policy-config */}
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1" data-testid="loan-policy-mode-toggle">
            <span className="text-xs font-semibold text-slate-600 px-2">Map policies by:</span>
            <button
              onClick={async () => {
                try {
                  await axios.put(`${API}/payroll/policy-config?company_id=${selectedCompany}`,
                    { policy_mapping_mode: 'designation' }, { withCredentials: true });
                  setMode('designation');
                  toast.success('Switched policy mapping to designation');
                } catch (e) {
                  toast.error('Mode change failed: ' + (e?.response?.data?.detail || e.message));
                }
              }}
              data-testid="loan-mode-designation-btn"
              className={`px-3 py-1.5 rounded text-xs font-semibold transition ${mode === 'designation' ? 'bg-white text-blue-700 shadow' : 'text-slate-600 hover:text-slate-900'}`}
            >Designation</button>
            <button
              onClick={async () => {
                try {
                  await axios.put(`${API}/payroll/policy-config?company_id=${selectedCompany}`,
                    { policy_mapping_mode: 'grade' }, { withCredentials: true });
                  setMode('grade');
                  toast.success('Switched policy mapping to grade');
                } catch (e) {
                  toast.error('Mode change failed: ' + (e?.response?.data?.detail || e.message));
                }
              }}
              data-testid="loan-mode-grade-btn"
              className={`px-3 py-1.5 rounded text-xs font-semibold transition ${mode === 'grade' ? 'bg-white text-blue-700 shadow' : 'text-slate-600 hover:text-slate-900'}`}
            >Grade</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left: designation OR grade list */}
          <Card className="p-3 lg:col-span-1">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">{keyLabelSingular}s</h3>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : keyItems.length === 0 ? (
              <p className="text-xs text-slate-500 italic">
                No {keyLabelSingular.toLowerCase()}s defined. Add them via Master Setup → {keyLabelSingular}s.
              </p>
            ) : (
              <ul className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid={`loan-${mode}-list`}>
                {keyItems.map(d => {
                  const matchField = mode === 'grade' ? 'grade_id' : 'designation_id';
                  const hasPolicy = policies.find(p => p[matchField] === d.id);
                  return (
                    <li key={d.id}>
                      <button
                        onClick={() => openPolicy(d.id)}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between hover:bg-blue-50 ${selectedKeyId === d.id ? 'bg-blue-100 font-medium' : ''}`}
                        data-testid={`loan-${mode}-${d.id}`}
                      >
                        <span>{d.name}</span>
                        {hasPolicy && (
                          <span className="text-emerald-600 text-[10px] font-bold">{hasPolicy.items.length}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* Right: policy editor */}
          <div className="lg:col-span-3 space-y-4">
            {!selectedKeyId ? (
              <Card className="p-8 text-center text-sm text-slate-500">
                Pick a {keyLabelSingular.toLowerCase()} from the left to define its advance policy.
              </Card>
            ) : (
              <>
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700">
                      Policy items ({items.length})
                    </h3>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={addItem} data-testid="loan-add-item-btn">
                        <Plus className="w-3 h-3 mr-1" /> Add Advance Type
                      </Button>
                      <Button size="sm" onClick={savePolicy} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="loan-save-policy-btn">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save Policy
                      </Button>
                      {policies.find(p => (mode === 'grade' ? p.grade_id : p.designation_id) === selectedKeyId) && (
                        <Button variant="outline" size="sm" onClick={deletePolicy} className="text-rose-600 border-rose-200">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {items.length === 0 ? (
                    <p className="text-xs text-slate-500 italic text-center py-6">
                      No advance types defined yet. Click &quot;Add Advance Type&quot; to define the first cap.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-2 w-1/5">Advance Type</th>
                            <th className="text-left p-2">Display Label</th>
                            <th className="text-right p-2">Max ₹ Principal</th>
                            <th className="text-right p-2">Min Tenure (mo)</th>
                            <th className="text-right p-2">Max Tenure (mo)</th>
                            <th className="text-right p-2">Default Rate %</th>
                            <th className="p-2"></th>
                          </tr>
                        </thead>
                        <tbody data-testid="loan-items-table">
                          {items.map((it, idx) => (
                            <tr key={idx} className="border-t border-slate-100" data-testid={`loan-item-row-${idx}`}>
                              <td className="p-1">
                                <select
                                  value={it.loan_type}
                                  onChange={e => updateItem(idx, { loan_type: e.target.value })}
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs"
                                  data-testid={`loan-item-type-${idx}`}
                                >
                                  {meta.loan_types.map(t => <option key={t} value={t}>{LOAN_LABEL_DEFAULTS[t] || t}</option>)}
                                </select>
                              </td>
                              <td className="p-1">
                                <input
                                  value={it.label || ''}
                                  onChange={e => updateItem(idx, { label: e.target.value })}
                                  placeholder={LOAN_LABEL_DEFAULTS[it.loan_type] || ''}
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs"
                                />
                              </td>
                              <td className="p-1">
                                <input
                                  type="number"
                                  value={it.max_principal}
                                  onChange={e => updateItem(idx, { max_principal: e.target.value })}
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs text-right"
                                  data-testid={`loan-item-principal-${idx}`}
                                />
                              </td>
                              <td className="p-1">
                                <input
                                  type="number"
                                  value={it.min_tenure_months}
                                  onChange={e => updateItem(idx, { min_tenure_months: e.target.value })}
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs text-right"
                                />
                              </td>
                              <td className="p-1">
                                <input
                                  type="number"
                                  value={it.max_tenure_months}
                                  onChange={e => updateItem(idx, { max_tenure_months: e.target.value })}
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs text-right"
                                  data-testid={`loan-item-tenure-${idx}`}
                                />
                              </td>
                              <td className="p-1">
                                <input
                                  type="number"
                                  step="0.1"
                                  value={it.interest_rate_default}
                                  onChange={e => updateItem(idx, { interest_rate_default: e.target.value })}
                                  className="w-full border border-slate-300 rounded px-1 py-1 text-xs text-right"
                                />
                              </td>
                              <td className="p-1 text-right">
                                <button onClick={() => removeItem(idx)} className="text-rose-500 hover:text-rose-700">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <label className="block mt-3 text-xs">
                    <span className="text-slate-700 font-medium">Notes</span>
                    <textarea
                      value={policyNotes}
                      onChange={e => setPolicyNotes(e.target.value)}
                      rows={2}
                      placeholder="e.g. Junior staff salary advance capped at 1-month gross"
                      className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                    />
                  </label>
                </Card>

                {/* Helper text */}
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-[11px] text-blue-900">
                  <p className="font-semibold mb-0.5">How this policy is enforced:</p>
                  <ul className="list-disc ml-4 space-y-0.5">
                    <li>When an employee submits a loan request, the engine matches their designation policy.</li>
                    <li>Disallowed loan_type → request flagged <b>needs_deviation_approval</b>.</li>
                    <li>Principal or tenure exceeds cap → same flag, routed to SPECIAL approval inbox.</li>
                    <li>Within all caps → standard manager &rarr; HR &rarr; finance flow.</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
